import {Injectable, Optional} from '@angular/core';

import * as JSZip from 'jszip';

import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {AssetApiService} from '@ngmedax/asset';

import {ApiService} from './api.service';


/**
 * Import/replace logic for thieme questionnaires. Adapted copy of the
 * questionnaire-grid import (the api service is not exported there) with two
 * key differences: the questionnaire keeps its stable thieme uuid from the mqt
 * (used as questionnaire and asset bucket id) and an existing questionnaire
 * plus its asset bucket are replaced via delete before create.
 */
@Injectable()
export class ThiemeImportService {

  public constructor(
    private apiService: ApiService,
    @Optional() private assetApi: AssetApiService) {
  }

  /**
   * Imports the given mqt blob. Replaces an existing questionnaire with the
   * same uuid including its asset bucket. Tolerates missing questionnaire or
   * bucket on first import.
   *
   * @param {Blob} blob mqt archive as blob
   * @param {string} fileName file name used for the bucket upload
   * @returns {Promise<Questionnaire>} imported questionnaire
   */
  public async import(blob: Blob, fileName: string = 'thieme.mqt'): Promise<Questionnaire> {
    const file = new File([blob], fileName, {type: 'application/zip'});
    // read the stable thieme uuid from the mqt - it replaces the random uuid of
    // the regular import so questionnaire id and asset bucket id stay coupled
    const uuid = await this.readQuestionnaireId(file);

    if (!uuid) {
      throw new Error('mqt archive contains no questionnaire id');
    }

    // replace flow: remove existing questionnaire and asset bucket first.
    // both calls must tolerate "does not exist" (first import)
    await this.ignoreNotFound(() => this.apiService.deleteQuestionnaire(uuid));
    this.assetApi && await this.ignoreNotFound(() => this.assetApi.removeBucket(uuid));

    let questionnaire: Questionnaire = null;

    if (this.assetApi) {
      const qFileName = 'questionnaire.json';
      const upload = this.assetApi.uploadBucketZip(uuid, file);
      await upload.promise;

      const payload: any = await this.assetApi.getBucketFileContent(uuid, qFileName);

      if (typeof payload !== 'object' || typeof payload.questionnaire !== 'object') {
        await this.ignoreNotFound(() => this.assetApi.removeBucket(uuid));
        throw new Error(`Found no questionnaire in created bucket with id: ${uuid}`);
      }

      questionnaire = payload.questionnaire;
      questionnaire.id = uuid;

      // hotfix to import old questionnaire format with broken options type
      questionnaire.meta && Array.isArray(questionnaire.meta.options) && (questionnaire.meta.options = {});

      await this.apiService.saveQuestionnaire(questionnaire);
      await this.assetApi.removeBucketFile(uuid, qFileName);
    }
    // fallback for when asset module is not present
    else {
      const zip = await JSZip.loadAsync(file);
      const content = await zip.file('questionnaire.json').async('string');
      const data = JSON.parse(content);

      if (typeof data.questionnaire === 'undefined') {
        throw new Error('Failed to unzip!');
      }

      data.questionnaire.id = uuid;
      questionnaire = await this.apiService.saveQuestionnaire(data.questionnaire);
    }

    return questionnaire;
  }

  /**
   * Reads the questionnaire id from the questionnaire.json inside the mqt blob.
   *
   * @param {Blob} file mqt archive as blob
   * @returns {Promise<string>}
   */
  private async readQuestionnaireId(file: Blob): Promise<string> {
    const zip = await JSZip.loadAsync(file);
    const qFile = zip.file('questionnaire.json');

    if (!qFile) {
      throw new Error('mqt archive contains no questionnaire.json');
    }

    const content = await qFile.async('string');
    const data = JSON.parse(content);
    const questionnaire = data && data.questionnaire ? data.questionnaire : null;

    return questionnaire && questionnaire.id ? questionnaire.id : null;
  }

  /**
   * Runs the given call and swallows "not found" errors (404) - needed for the
   * first import where neither questionnaire nor bucket exist yet.
   *
   * @param {Function} call
   */
  private async ignoreNotFound(call: () => Promise<any>): Promise<void> {
    try {
      await call();
    } catch (error: any) {
      const status = error && (error.status || (error.error && error.error.code));

      if (status === 404) {
        return;
      }

      throw error;
    }
  }
}
